import { DataStore } from 'aws-amplify';
import { TripSheet, SSCC } from '../models';
import { logAdminAction, AdminActionType } from './auditLogger';
import { ManifestStatus } from '../datastore/tripSheets';
import { updateManifestAsnsCoupa, createSiteReceiptCoupa } from '../common/helperFunctions/coupa';
import { UpdateAsnProps, InventoryLineItem } from '../api/postQM';
import { mapToReceiptedItems } from '../pages/receive-manifest/details/detail.mapping';

/**
 * Admin service for manifest management operations
 * All operations log to AdminActionHistory for audit trail
 */

interface CompleteManifestOptions {
  tripSheetId: string;
  manifestId: string;
  reason: string;
  actionBy: string;
}

interface DeleteManifestOptions {
  tripSheetId: string;
  manifestId: string;
  legId: string;
  reason: string;
  actionBy: string;
}

interface AddSSCCToManifestOptions {
  tripSheetId: string;
  manifestId: string;
  legId: string;
  ssccId: string;
  reason: string;
  actionBy: string;
}

interface RemoveSSCCFromManifestOptions {
  tripSheetId: string;
  manifestId: string;
  legId: string;
  ssccId: string;
  reason: string;
  actionBy: string;
}

/**
 * Marks a manifest as completed
 * Updates all pending inventory transactions and ASN statuses
 */
export async function completeManifest(options: CompleteManifestOptions): Promise<{
  success: boolean;
  message: string;
  error?: any;
}> {
  const { tripSheetId, manifestId, reason, actionBy } = options;

  try {
    // 1. Fetch the trip sheet
    const tripSheet = await DataStore.query(TripSheet, tripSheetId);
    if (!tripSheet) {
      throw new Error(`Trip sheet ${tripSheetId} not found`);
    }

    // 2. Find the manifest and leg
    let foundManifest: any = null;
    let foundLeg: any = null;
    let legIndex = -1;
    let manifestIndex = -1;

    for (let i = 0; i < (tripSheet.legs || []).length; i++) {
      const leg = tripSheet.legs![i];
      if (!leg?.manifests) continue;

      for (let j = 0; j < leg.manifests.length; j++) {
        const manifest = leg.manifests[j];
        if (manifest?.id === manifestId) {
          foundManifest = manifest;
          foundLeg = leg;
          legIndex = i;
          manifestIndex = j;
          break;
        }
      }
      if (foundManifest) break;
    }

    if (!foundManifest || !foundLeg) {
      throw new Error(`Manifest ${manifestId} not found in trip sheet`);
    }

    // 3. Capture before state for audit
    const beforeState = {
      manifestId: foundManifest.id,
      status: foundManifest.status,
      tripId: tripSheet.id,
      ssccs: foundManifest.ssccs?.map((s: any) => s?.ssccId),
      asns: extractASNsFromManifest(foundManifest)
    };

    // 4. Update manifest status to completed
    const updatedManifest = {
      ...foundManifest,
      status: ManifestStatus.completed
    };

    // 5. Update the trip sheet with new manifest data
    const updatedLegs = [...(tripSheet.legs || [])];
    const updatedManifests = [...(updatedLegs[legIndex]?.manifests || [])];
    updatedManifests[manifestIndex] = updatedManifest;
    updatedLegs[legIndex] = {
      ...foundLeg,
      manifests: updatedManifests
    };

    await DataStore.save(
      TripSheet.copyOf(tripSheet, (draft) => {
        draft.legs = updatedLegs;
      })
    );

    // 6. Publish ftl_site_receipt events to zero out GIW stock-on-hand
    try {
      const receiptedItems = mapToReceiptedItems(updatedManifest);

      if (receiptedItems.length > 0) {
        console.log(`Publishing ${receiptedItems.length} ftl_site_receipt event(s) to zero out GIW inventory`);

        for (const receiptedItem of receiptedItems) {
          const receiptResponse = await createSiteReceiptCoupa(receiptedItem);
          if (receiptResponse.error) {
            console.error('Error publishing ftl_site_receipt for item:', receiptedItem.id, receiptResponse.error);
            // Continue with other items even if one fails
          } else {
            console.log(`Successfully published ftl_site_receipt for item ${receiptedItem.id}`);
          }
        }
      } else {
        console.log('No inventory items to receipt - skipping ftl_site_receipt events');
      }
    } catch (receiptError) {
      console.error('Failed to publish ftl_site_receipt events:', receiptError);
      console.warn('GIW inventory may not be zeroed out. Manual reconciliation may be required.');
    }

    // 7. Update ASN statuses in Coupa to "Available"
    try {
      const asnsToUpdate = extractASNsFromManifest(updatedManifest);
      const filteredAsns = asnsToUpdate.filter((asn): asn is string => asn !== null && asn !== undefined && asn !== '');

      // Extract inventory line items to update GIW stock-on-hand to zero
      const inventoryLines = extractInventoryLinesFromManifest(updatedManifest);

      if (filteredAsns.length > 0) {
        const asnUpdateEvent: UpdateAsnProps = {
          asns: filteredAsns,
          inventoryLines: inventoryLines.length > 0 ? inventoryLines : undefined
        };
        await updateManifestAsnsCoupa(asnUpdateEvent);
        console.log(`Successfully updated ${filteredAsns.length} ASN(s) to Available status in Coupa`);
        if (inventoryLines.length > 0) {
          console.log(`Sent ${inventoryLines.length} inventory line item(s) with ftl_manifest_completed event`);
        }
      }
    } catch (coupaError) {
      // Log the error but don't fail the operation
      // The manifest is still marked as completed in FTL
      console.error('Failed to update ASN statuses in Coupa:', coupaError);
      console.warn('Manifest marked as completed in FTL, but Coupa ASN update failed. ASNs may need manual update.');
    }

    // 8. Capture after state for audit
    const afterState = {
      manifestId: updatedManifest.id,
      status: updatedManifest.status,
      tripId: tripSheet.id,
      ssccs: updatedManifest.ssccs?.map((s: any) => s?.ssccId),
      asns: extractASNsFromManifest(updatedManifest)
    };

    // 9. Log to audit trail
    await logAdminAction({
      actionType: AdminActionType.MANIFEST_COMPLETE,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy,
      reason
    });

    return {
      success: true,
      message: `Manifest ${manifestId} marked as completed successfully`
    };
  } catch (error) {
    console.error('Failed to complete manifest:', error);
    return {
      success: false,
      message: `Failed to complete manifest: ${error}`,
      error
    };
  }
}

/**
 * Deletes a manifest from a trip sheet
 * Marks the manifest as deleted (soft delete)
 */
export async function deleteManifest(options: DeleteManifestOptions): Promise<{
  success: boolean;
  message: string;
  error?: any;
}> {
  const { tripSheetId, manifestId, legId, reason, actionBy } = options;

  try {
    // 1. Fetch the trip sheet
    const tripSheet = await DataStore.query(TripSheet, tripSheetId);
    if (!tripSheet) {
      throw new Error(`Trip sheet ${tripSheetId} not found`);
    }

    // 2. Find the leg and manifest
    let foundManifest: any = null;
    let foundLeg: any = null;
    let legIndex = -1;
    let manifestIndex = -1;

    for (let i = 0; i < (tripSheet.legs || []).length; i++) {
      const leg = tripSheet.legs![i];
      if (leg?.id !== legId || !leg?.manifests) continue;

      for (let j = 0; j < leg.manifests.length; j++) {
        const manifest = leg.manifests[j];
        if (manifest?.id === manifestId) {
          foundManifest = manifest;
          foundLeg = leg;
          legIndex = i;
          manifestIndex = j;
          break;
        }
      }
      if (foundManifest) break;
    }

    if (!foundManifest || !foundLeg) {
      throw new Error(`Manifest ${manifestId} not found in leg ${legId}`);
    }

    // 3. Capture before state for audit
    const beforeState = {
      manifestId: foundManifest.id,
      status: foundManifest.status,
      tripId: tripSheet.id,
      legId: foundLeg.id,
      ssccs: foundManifest.ssccs?.map((s: any) => s?.ssccId),
      asns: extractASNsFromManifest(foundManifest)
    };

    // 4. Remove the manifest from the leg
    const updatedLegs = [...(tripSheet.legs || [])];
    const updatedManifests = [...(updatedLegs[legIndex]?.manifests || [])];
    updatedManifests.splice(manifestIndex, 1); // Remove manifest
    updatedLegs[legIndex] = {
      ...foundLeg,
      manifests: updatedManifests
    };

    await DataStore.save(
      TripSheet.copyOf(tripSheet, (draft) => {
        draft.legs = updatedLegs;
      })
    );

    // 5. Capture after state for audit
    const afterState = {
      manifestId: foundManifest.id,
      status: 'DELETED',
      tripId: tripSheet.id,
      legId: foundLeg.id,
      deleted: true
    };

    // 6. Log to audit trail
    await logAdminAction({
      actionType: AdminActionType.MANIFEST_DELETE,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy,
      reason
    });

    return {
      success: true,
      message: `Manifest ${manifestId} deleted successfully`
    };
  } catch (error) {
    console.error('Failed to delete manifest:', error);
    return {
      success: false,
      message: `Failed to delete manifest: ${error}`,
      error
    };
  }
}

/**
 * Adds an SSCC to a manifest
 * SSCC must be in staged status
 */
export async function addSSCCToManifest(options: AddSSCCToManifestOptions): Promise<{
  success: boolean;
  message: string;
  error?: any;
}> {
  const { tripSheetId, manifestId, legId, ssccId, reason, actionBy } = options;

  try {
    // 1. Fetch the SSCC and validate it's in staged status
    const sscc = await DataStore.query(SSCC, ssccId);
    if (!sscc) {
      throw new Error(`SSCC ${ssccId} not found`);
    }

    if (sscc.status !== 'staged') {
      throw new Error(`SSCC ${ssccId} must be in 'staged' status. Current status: ${sscc.status}`);
    }

    // 2. Fetch the trip sheet
    const tripSheet = await DataStore.query(TripSheet, tripSheetId);
    if (!tripSheet) {
      throw new Error(`Trip sheet ${tripSheetId} not found`);
    }

    // 3. Find the manifest and leg
    let foundManifest: any = null;
    let foundLeg: any = null;
    let legIndex = -1;
    let manifestIndex = -1;

    for (let i = 0; i < (tripSheet.legs || []).length; i++) {
      const leg = tripSheet.legs![i];
      if (leg?.id !== legId || !leg?.manifests) continue;

      for (let j = 0; j < leg.manifests.length; j++) {
        const manifest = leg.manifests[j];
        if (manifest?.id === manifestId) {
          foundManifest = manifest;
          foundLeg = leg;
          legIndex = i;
          manifestIndex = j;
          break;
        }
      }
      if (foundManifest) break;
    }

    if (!foundManifest || !foundLeg) {
      throw new Error(`Manifest ${manifestId} not found in leg ${legId}`);
    }

    // 4. Check if SSCC is already in manifest
    const existingSsccs = foundManifest.ssccs || [];
    if (existingSsccs.some((s: any) => s?.ssccId === ssccId)) {
      throw new Error(`SSCC ${ssccId} is already in manifest ${manifestId}`);
    }

    // 5. Capture before state for audit
    const beforeState = {
      manifestId: foundManifest.id,
      ssccs: existingSsccs.map((s: any) => s?.ssccId),
      sscc: {
        id: sscc.id,
        status: sscc.status
      }
    };

    // 6. Add SSCC to manifest (create ManifestSSCC structure)
    const newManifestSSCC = {
      ssccId: sscc.id,
      status: foundManifest.status, // Adopt manifest status
      packages: [], // Empty packages initially
      totalWeight: parseFloat(sscc.weight || '0')
    };

    const updatedManifest = {
      ...foundManifest,
      ssccs: [...existingSsccs, newManifestSSCC]
    };

    // 7. Update the trip sheet
    const updatedLegs = [...(tripSheet.legs || [])];
    const updatedManifests = [...(updatedLegs[legIndex]?.manifests || [])];
    updatedManifests[manifestIndex] = updatedManifest;
    updatedLegs[legIndex] = {
      ...foundLeg,
      manifests: updatedManifests
    };

    await DataStore.save(
      TripSheet.copyOf(tripSheet, (draft) => {
        draft.legs = updatedLegs;
      })
    );

    // 8. Update SSCC status to manifested
    await DataStore.save(
      SSCC.copyOf(sscc, (draft) => {
        draft.status = 'manifested';
        draft.manifested = new Date().toISOString();
      })
    );

    // 9. Capture after state for audit
    const afterState = {
      manifestId: updatedManifest.id,
      ssccs: updatedManifest.ssccs.map((s: any) => s?.ssccId),
      sscc: {
        id: sscc.id,
        status: 'manifested'
      }
    };

    // 10. Log to audit trail
    await logAdminAction({
      actionType: AdminActionType.SSCC_ADD_TO_MANIFEST,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy,
      reason
    });

    return {
      success: true,
      message: `SSCC ${ssccId} added to manifest ${manifestId} successfully`
    };
  } catch (error) {
    console.error('Failed to add SSCC to manifest:', error);
    return {
      success: false,
      message: `Failed to add SSCC to manifest: ${error}`,
      error
    };
  }
}

/**
 * Removes an SSCC from a manifest
 * Returns SSCC to staged status
 */
export async function removeSSCCFromManifest(options: RemoveSSCCFromManifestOptions): Promise<{
  success: boolean;
  message: string;
  error?: any;
}> {
  const { tripSheetId, manifestId, legId, ssccId, reason, actionBy } = options;

  try {
    // 1. Fetch the SSCC
    const sscc = await DataStore.query(SSCC, ssccId);
    if (!sscc) {
      throw new Error(`SSCC ${ssccId} not found`);
    }

    // 2. Fetch the trip sheet
    const tripSheet = await DataStore.query(TripSheet, tripSheetId);
    if (!tripSheet) {
      throw new Error(`Trip sheet ${tripSheetId} not found`);
    }

    // 3. Find the manifest and leg
    let foundManifest: any = null;
    let foundLeg: any = null;
    let legIndex = -1;
    let manifestIndex = -1;

    for (let i = 0; i < (tripSheet.legs || []).length; i++) {
      const leg = tripSheet.legs![i];
      if (leg?.id !== legId || !leg?.manifests) continue;

      for (let j = 0; j < leg.manifests.length; j++) {
        const manifest = leg.manifests[j];
        if (manifest?.id === manifestId) {
          foundManifest = manifest;
          foundLeg = leg;
          legIndex = i;
          manifestIndex = j;
          break;
        }
      }
      if (foundManifest) break;
    }

    if (!foundManifest || !foundLeg) {
      throw new Error(`Manifest ${manifestId} not found in leg ${legId}`);
    }

    // 4. Find and remove the SSCC from manifest
    const existingSsccs = foundManifest.ssccs || [];
    const ssccIndex = existingSsccs.findIndex((s: any) => s?.ssccId === ssccId);

    if (ssccIndex === -1) {
      throw new Error(`SSCC ${ssccId} not found in manifest ${manifestId}`);
    }

    const removedSSCC = existingSsccs[ssccIndex];

    // 5. Capture before state for audit
    const beforeState = {
      manifestId: foundManifest.id,
      ssccs: existingSsccs.map((s: any) => s?.ssccId),
      sscc: {
        id: sscc.id,
        status: sscc.status
      },
      asnsInSSCC: removedSSCC.packages?.map((p: any) => p?.asn) || []
    };

    // 6. Remove SSCC from manifest
    const updatedSsccs = [...existingSsccs];
    updatedSsccs.splice(ssccIndex, 1);

    const updatedManifest = {
      ...foundManifest,
      ssccs: updatedSsccs
    };

    // 7. Update the trip sheet
    const updatedLegs = [...(tripSheet.legs || [])];
    const updatedManifests = [...(updatedLegs[legIndex]?.manifests || [])];
    updatedManifests[manifestIndex] = updatedManifest;
    updatedLegs[legIndex] = {
      ...foundLeg,
      manifests: updatedManifests
    };

    await DataStore.save(
      TripSheet.copyOf(tripSheet, (draft) => {
        draft.legs = updatedLegs;
      })
    );

    // 8. Update SSCC status back to staged
    await DataStore.save(
      SSCC.copyOf(sscc, (draft) => {
        draft.status = 'staged';
        draft.manifested = undefined; // Clear manifested timestamp
      })
    );

    // 9. Capture after state for audit
    const afterState = {
      manifestId: updatedManifest.id,
      ssccs: updatedManifest.ssccs.map((s: any) => s?.ssccId),
      sscc: {
        id: sscc.id,
        status: 'staged'
      }
    };

    // 10. Log to audit trail
    await logAdminAction({
      actionType: AdminActionType.SSCC_REMOVE_FROM_MANIFEST,
      beforeAction: beforeState,
      afterAction: afterState,
      actionBy,
      reason
    });

    // TODO: Update ASN statuses in Coupa via API
    // This should call the Coupa API to update ASN statuses back to 'staged'

    return {
      success: true,
      message: `SSCC ${ssccId} removed from manifest ${manifestId} and returned to staged status`
    };
  } catch (error) {
    console.error('Failed to remove SSCC from manifest:', error);
    return {
      success: false,
      message: `Failed to remove SSCC from manifest: ${error}`,
      error
    };
  }
}

/**
 * Helper function to extract all ASNs from a manifest
 */
function extractASNsFromManifest(manifest: any): string[] {
  const asnSet = new Set<string>();

  (manifest.ssccs || []).forEach((sscc: any) => {
    (sscc?.packages || []).forEach((pkg: any) => {
      if (pkg?.asn) {
        asnSet.add(pkg.asn);
      }
    });
  });

  return Array.from(asnSet);
}

/**
 * Helper function to extract all inventory line items from a manifest
 * These are used to update GIW (Goods-In-Warehouse) stock-on-hand to zero
 */
function extractInventoryLinesFromManifest(manifest: any): InventoryLineItem[] {
  const inventoryLines: InventoryLineItem[] = [];

  (manifest.ssccs || []).forEach((sscc: any) => {
    (sscc?.packages || []).forEach((pkg: any) => {
      (pkg?.inventoryLines || []).forEach((line: any) => {
        if (line && line.inTransitWarehouseId && line.inTransitWarehouseLocationId) {
          inventoryLines.push({
            orderLineId: line.orderLineId,
            partId: line.partId,
            sourcePartId: line.sourcePartId,
            description: line.description,
            quantityReceived: line.quantityReceived,
            inTransitWarehouseId: line.inTransitWarehouseId,
            inTransitWarehouseLocationId: line.inTransitWarehouseLocationId
          });
        }
      });
    });
  });

  return inventoryLines;
}
